import { ApiErrorCode } from '@hprofits/api/common/errors';
import { isTransparentError } from '@hprofits/api-client/error-handling';
import type { ApiError } from '@hprofits/api-client/errors';
import { extractApiError, HttpError, NetworkError } from '@hprofits/api-client/errors';
import { HttpStatus } from '@hprofits/utils';

import { extractValidationErrorMessage } from '../errors';

type ErrorCodeMessageMap = Partial<Record<ApiErrorCode, string>>;

export type MutationErrorOverride = (error: ApiError) => string | undefined;

/**
 * Prepends server-provided field-level validation details (REQUEST_BODY_VALIDATION
 * issues) to a per-entity override. Use when the page surfaces validation errors
 * inline in the same panel as classified mutation errors.
 */
export const withValidationFirst =
  (override: MutationErrorOverride): MutationErrorOverride =>
  (error) =>
    extractValidationErrorMessage(error) ?? override(error);

export type MutationErrorResolverSpec = Readonly<{
  defaultMessage: string;
  codeMessages?: ErrorCodeMessageMap;
  /**
   * Shown for an HTTP 403. Access control never travels as an `ApiError`: the backend maps
   * `OperationForbiddenError` to a bare `ORPCError('FORBIDDEN', { message: 'Forbidden' })` so no
   * server-side reason leaks, which leaves the caller to name the one rule it can be about.
   */
  forbiddenMessage?: string;
}>;

export const buildErrorHandlerCampaignMutation =
  (spec: MutationErrorResolverSpec) =>
  (error: unknown): string => {
    const apiError = extractApiError(error);

    const codeMessage = apiError ? spec.codeMessages?.[apiError.errorCode] : undefined;
    if (codeMessage) {
      return codeMessage;
    }

    // `ApiError extends HttpError`, but no ApiError carries 403 (see the error catalog), so the
    // code branch above always wins where both could match.
    if (spec.forbiddenMessage !== undefined && error instanceof HttpError && error.status === HttpStatus.FORBIDDEN) {
      return spec.forbiddenMessage;
    }

    if (error instanceof NetworkError) {
      return error.message;
    }

    return spec.defaultMessage;
  };

type CreateMutationErrorMessageSpec = Readonly<{
  defaultMessage: string;
  duplicateMessage?: string;
  notFoundMessage?: string;
  expectationFailedMessage?: string;
  forbiddenMessage?: string;
}>;

type SaveMutationErrorMessageSpec = Readonly<{
  defaultMessage: string;
  duplicateMessage?: string;
  notFoundMessage?: string;
  immutableMessage?: string;
  expectationFailedMessage?: string;
  inUseMessage?: string;
}>;

type DeleteMutationErrorMessageSpec = Readonly<{
  defaultMessage: string;
  notFoundMessage?: string;
  inUseMessage?: string;
}>;

const buildCodeMessages = (
  entries: ReadonlyArray<readonly [ApiErrorCode, string | undefined]>,
): ErrorCodeMessageMap => {
  return Object.fromEntries(
    entries.filter((entry): entry is readonly [ApiErrorCode, string] => entry[1] !== undefined),
  );
};

export const buildCreateMutationErrorMessage = (spec: CreateMutationErrorMessageSpec) =>
  buildErrorHandlerCampaignMutation({
    defaultMessage: spec.defaultMessage,
    ...(spec.forbiddenMessage === undefined ? {} : { forbiddenMessage: spec.forbiddenMessage }),
    codeMessages: buildCodeMessages([
      [ApiErrorCode.ENTITY_ALREADY_EXISTS, spec.duplicateMessage],
      [ApiErrorCode.ENTITY_NOT_FOUND, spec.notFoundMessage],
      [ApiErrorCode.EXPECTATION_FAILED, spec.expectationFailedMessage],
    ]),
  });

export const buildSaveMutationErrorMessage = (spec: SaveMutationErrorMessageSpec) =>
  buildErrorHandlerCampaignMutation({
    defaultMessage: spec.defaultMessage,
    codeMessages: buildCodeMessages([
      [ApiErrorCode.ENTITY_ALREADY_EXISTS, spec.duplicateMessage],
      [ApiErrorCode.ENTITY_NOT_FOUND, spec.notFoundMessage],
      [ApiErrorCode.FIELD_CANNOT_BE_UPDATED, spec.immutableMessage],
      [ApiErrorCode.EXPECTATION_FAILED, spec.expectationFailedMessage],
    ]),
  });

export const buildDeleteMutationErrorMessage = (spec: DeleteMutationErrorMessageSpec) =>
  buildErrorHandlerCampaignMutation({
    defaultMessage: spec.defaultMessage,
    codeMessages: buildCodeMessages([
      [ApiErrorCode.ENTITY_NOT_FOUND, spec.notFoundMessage],
      [ApiErrorCode.ENTITY_IN_USE, spec.inUseMessage],
    ]),
  });

export const buildCreateMutationErrorOverride = (
  spec: Omit<CreateMutationErrorMessageSpec, 'defaultMessage'>,
): MutationErrorOverride => {
  const codeMessages = buildCodeMessages([
    [ApiErrorCode.ENTITY_ALREADY_EXISTS, spec.duplicateMessage],
    [ApiErrorCode.ENTITY_NOT_FOUND, spec.notFoundMessage],
    [ApiErrorCode.EXPECTATION_FAILED, spec.expectationFailedMessage],
  ]);
  return (error) => codeMessages[error.errorCode];
};

export const buildSaveMutationErrorOverride = (
  spec: Omit<SaveMutationErrorMessageSpec, 'defaultMessage'>,
): MutationErrorOverride => {
  const codeMessages = buildCodeMessages([
    [ApiErrorCode.ENTITY_ALREADY_EXISTS, spec.duplicateMessage],
    [ApiErrorCode.ENTITY_NOT_FOUND, spec.notFoundMessage],
    [ApiErrorCode.FIELD_CANNOT_BE_UPDATED, spec.immutableMessage],
    [ApiErrorCode.EXPECTATION_FAILED, spec.expectationFailedMessage],
    [ApiErrorCode.ENTITY_IN_USE, spec.inUseMessage],
  ]);
  return (error) => codeMessages[error.errorCode];
};

export const buildDeleteMutationErrorOverride = (
  spec: Omit<DeleteMutationErrorMessageSpec, 'defaultMessage'>,
): MutationErrorOverride => {
  const codeMessages = buildCodeMessages([
    [ApiErrorCode.ENTITY_NOT_FOUND, spec.notFoundMessage],
    [ApiErrorCode.ENTITY_IN_USE, spec.inUseMessage],
  ]);
  return (error) => codeMessages[error.errorCode];
};

/**
 * Resolves an error to a user-facing message.
 * - classifiable ApiError (override match or transparent code) → specific message
 * - everything else (5xx, NetworkError, JS error) → contextual `genericMessage`
 */
export const formatMutationError = (
  error: unknown,
  override: MutationErrorOverride,
  genericMessage: string,
): string => {
  const apiError = extractApiError(error);
  if (apiError) {
    const overrideMessage = override(apiError);
    if (overrideMessage) {
      return overrideMessage;
    }
    if (isTransparentError(apiError.errorCode)) {
      return apiError.message;
    }
  }
  return genericMessage;
};
